/* eslint-disable class-methods-use-this */
import Promise from 'bluebird';
import { PlayerTech } from './constants';
import PlayerBase from './player-tech/PlayerBase';
import BAMPlayer from './player-tech/BAMPlayer';
import BrightcovePlayer from './player-tech/BrightcovePlayer';

class WatchESPNWebPlayer extends PlayerBase {
    constructor({ bamPlayerOptions = {}, brightcovePlayerOptions = {} } = {}) {
        super();
        this._bamPlayer = null;
        this._brightcovePlayer = null;
        this._bamPlayerOptions = bamPlayerOptions;
        this._brightcovePlayerOptions = brightcovePlayerOptions;
    }
    create({ playerTech, container, ...options }) {
        let promise = Promise.resolve();
        if (playerTech === PlayerTech.BAM) {
            if (!this._bamPlayer) {
                this._bamPlayer = new BAMPlayer(this._bamPlayerOptions);
                Object.keys(BAMPlayer.Events).forEach(event => {
                    this._bamPlayer.on(
                        BAMPlayer.Events[event],
                        this.proxyEvent.bind(this, BAMPlayer.Events[event])
                    );
                });
            }
            if (this._player && this._player.tech !== PlayerTech.BAM) {
                promise = this.delete();
            }
            this._player = this._bamPlayer;
        } else if (playerTech === PlayerTech.BRIGHTCOVE) {
            if (!this._brightcovePlayer) {
                this._brightcovePlayer = new BrightcovePlayer(this._brightcovePlayerOptions);
                Object.keys(BrightcovePlayer.Events).forEach(event => {
                    this._brightcovePlayer.on(
                        BrightcovePlayer.Events[event],
                        this.proxyEvent.bind(this, BrightcovePlayer.Events[event])
                    );
                });
            }
            if (this._player && this._player.tech !== PlayerTech.BRIGHTCOVE) {
                promise = this.delete();
            }
            this._player = this._brightcovePlayer;
        }
        if (!this._player) {
            return Promise.reject(new Error('cannot create player.'));
        }

        let containerEl;
        try {
            if (typeof container === 'string') {
                containerEl = document.querySelector(container);
            } else {
                containerEl = container;
            }
        } catch (err) {
            return Promise.reject(err);
        }

        const playerOptions = {
            ...options,
            selector: container,
            el: containerEl
        };

        return promise.then(() => this._player.create(playerOptions)).then(() => {
            this.emit(WatchESPNWebPlayer.Events.READY);
        });
    }

    destroy() {
        if (!this._player) {
            return Promise.resolve(this);
        }

        if (this._bamPlayer && this._player.destroyPlayer) {
            return this._player.destroyPlayer().then(() => {
                this._bamPlayer.removeAllListeners();
            });
        }

        return this.delete().then(() => {
            this._brightcovePlayer.removeAllListeners();
            this._brightcovePlayer = null;
        });
    }

    getPlayer(tech) {
        switch (tech) {
            case PlayerTech.BRIGHTCOVE:
                return this._brightcovePlayer;
            case PlayerTech.BAM:
                return this._bamPlayer;
            default: // return currently active player
                return this._player;
        }
    }

    getGeoLocation(options) {
        const player = new BAMPlayer(this._bamPlayerOptions);
        player.once(
            BAMPlayer.Events.GEOLOCATION_READY,
            this.proxyEvent.bind(this, BAMPlayer.Events.GEOLOCATION_READY)
        );
        return player.getGeoLocation({ ...options, selector: options.container });
    }

    renderPaywall(options) {
        const player = new BAMPlayer(this._bamPlayerOptions);
        player.once(
            BAMPlayer.Events.LOGIN_REQUESTED,
            this.proxyEvent.bind(this, BAMPlayer.Events.LOGIN_REQUESTED)
        );
        return player.renderPaywall({ ...options, selector: options.container });
    }

    createSDKWrapper(options) {
        const player = new BAMPlayer(this._bamPlayerOptions);
        player.once(
            BAMPlayer.Events.SUBSCRIPTIONS_READY,
            this.proxyEvent.bind(this, BAMPlayer.Events.SUBSCRIPTIONS_READY)
        );
        player.once(
            BAMPlayer.Events.GEOLOCATION_READY,
            this.proxyEvent.bind(this, BAMPlayer.Events.GEOLOCATION_READY)
        );
        player.once(
            BAMPlayer.Events.SESSIONTOKEN_READY,
            this.proxyEvent.bind(this, BAMPlayer.Events.SESSIONTOKEN_READY)
        );

        player.once(
            BAMPlayer.Events.USER_TRACKING_INFORMATION_READY,
            this.proxyEvent.bind(this, BAMPlayer.Events.USER_TRACKING_INFORMATION_READY)
        );

        window.sdkWrapper = player;
        return player.createSDKWrapper({ ...options, selector: options.container });
    }

    sendContentReward({ selector, rewardToken, contentId, userActivityAction, recommendedContentIds }) {
        if (window.sdkWrapper) {
            return window.sdkWrapper.sendContentReward(
                selector,
                rewardToken,
                contentId,
                userActivityAction,
                recommendedContentIds
            );
        }

        return Promise.resolve(this);
    }

    delete() {
        if (!this._player) {
            return Promise.resolve(this);
        }
        return this._player.delete()
            .then(() => {
                this._player = null;
            });
    }

    loadMedia(options) {
        if (!this._player) {
            return Promise.resolve(this);
        }
        return this._player.loadMedia({ ...options, selector: options.container });
    }

    changeFeed(options) {
        if (!this._player) {
            return Promise.resolve(this);
        }
        return this._player.changeFeed({ ...options, selector: options.container });
    }

    deactivate(...args) {
        if (!this._player) {
            return Promise.resolve(this);
        }
        return this._player.deactivate(...args);
    }

    play() {
        if (!this._player) {
            return Promise.resolve(this);
        }
        return this._player.play();
    }

    pause() {
        if (!this._player) {
            return Promise.resolve(this);
        }
        return this._player.pause();
    }

    mute() {
        if (!this._player) {
            return Promise.resolve(this);
        }
        return this._player.mute();
    }

    unmute() {
        if (!this._player) {
            return Promise.resolve(this);
        }
        return this._player.unmute();
    }

    error(...args) {
        if (!this._player) {
            return Promise.resolve(this);
        }
        return this._player.error(...args);
    }

    getPlayerConfigs() {
        if (!this._player) {
            return Promise.resolve();
        }
        return this._player.getPlayerConfigs();
    }

    updatePlayerConfigs(...args) {
        if (!this._player) {
            return Promise.resolve(this);
        }
        return this._player.updatePlayerConfigs(...args);
    }

    proxyEvent(type, ...args) {
        this.emit(type, ...args);
    }
}

WatchESPNWebPlayer.Events = {
    READY: 'READY',
    ...BrightcovePlayer.Events,
    ...BAMPlayer.Events
};

WatchESPNWebPlayer.PlayerTech = PlayerTech;

export default WatchESPNWebPlayer;



// WEBPACK FOOTER //
// ./app/webapp/common/video-player/index.js